import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  Avatar,
  Dialog,
  DialogContent,
  DialogTitle,
  useMediaQuery,
  InputAdornment,
  IconButton,
  DialogContentText,
  Tooltip,
} from '@mui/material';
import { ManageAccounts, Close, DeleteOutline } from '@mui/icons-material';
import QrCodeGenerator from './QrCodeGenerator';
import { poppins300 } from 'theme/common.style';
import { useHistory } from 'react-router-dom';
import Arrow1 from 'assets/images/Arrow1.png';
import { API_URL, Basename_url, PROFILE_SETTING } from 'constant/app.config';
import Swal from 'sweetalert2';
import { postApiCall } from 'api/apiService';
import { useDropzone } from 'react-dropzone';
import { useTheme } from '@emotion/react';
import PreviewImage from './PreviewImage';
import Loading from 'assets/images/loading.png';
import ViewSignature from './ViewSignature';
import NameTooltip from 'component/Common/NameTooltip';

const MAX_FILE_SIZE = 5 * 1024 * 1024;

const EditIcon = () => {
  return (
    <svg
      fill="#9e9d9d"
      version="1.1"
      id="Capa_1"
      xmlns="http://www.w3.org/2000/svg"
      width="15px"
      height="15px"
      viewBox="0 0 528.899 528.899"
    >
      <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
      <g
        id="SVGRepo_tracerCarrier"
        strokeLinecap="round"
        strokeLinejoin="round"
      ></g>
      <g id="SVGRepo_iconCarrier">
        {' '}
        <g>
          {' '}
          <path d="M328.883,89.125l107.59,107.589l-272.34,272.34L56.604,361.465L328.883,89.125z M518.113,63.177l-47.981-47.981 c-18.543-18.543-48.653-18.543-67.259,0l-45.961,45.961l107.59,107.59l53.611-53.611 C532.495,100.753,532.495,77.559,518.113,63.177z M0.3,512.69c-1.958,8.812,5.998,16.708,14.811,14.565l119.891-29.069 L27.473,390.597L0.3,512.69z"></path>{' '}
        </g>{' '}
      </g>
    </svg>
  );
};

const TickIcon = () => {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
      <g
        id="SVGRepo_tracerCarrier"
        strokeLinecap="round"
        strokeLinejoin="round"
      ></g>
      <g id="SVGRepo_iconCarrier">
        <path
          d="M4.89163 13.2687L9.16582 17.5427L18.7085 8"
          stroke="#6C2AD2"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
    </svg>
  );
};

const formatMobileNumber = (value) => {
  if (!value) return '';
  const digits = value.replace(/\D/g, '').slice(0, 10);
  const match = digits.match(/^(\d{0,4})(\d{0,3})(\d{0,3})$/);
  if (!match) return digits;
  return [match[1], match[2], match[3]].filter(Boolean).join(' ');
};

const MOBILE_REGEX = /^04\d{2}\s?\d{3}\s?\d{3}$/;

const ProfileSettings = ({ solicitor, setOpenprofile }) => {
  const history = useHistory();

  const [profilePic, setProfilePic] = useState('');
  const [profileFile, setProfileFile] = useState(null);

  const [openQR, setOpenQR] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [rejectionMessage, setRejectionMessage] = useState('');
  const [editableField, setEditableField] = useState(null);
  const [initialData, setInitialData] = useState(null);
  const [initialProfilePic, setInitialProfilePic] = useState(null);
  const [originalImageFile, setOriginalImageFile] = useState(null);
  const [openImageViewer, setOpenImageViewer] = useState(false);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    mobileNumber: '',
    userType: '',
    email: '',
    agencyName: '',
    userId: '',
    qrCodeValue: '',
  });

  const [errors, setErrors] = useState({});
  const [openPreview, setOpenPreview] = useState(false);

  const inputRefs = {
    firstName: useRef(null),
    lastName: useRef(null),
    mobileNumber: useRef(null),
  };

  const hasUnsavedChanges = () => {
    const fieldsChanged = ['firstName', 'lastName', 'mobileNumber'].some(
      (key) => formData[key] !== (initialData?.[key] ?? '')
    );

    return (
      fieldsChanged ||
      profilePic !== (initialProfilePic ?? '') ||
      profileFile !== null
    );
  };

  const hasEditedChanges = hasUnsavedChanges();

  const handleBack = () => {
    if (isUploading) return;
    if (hasUnsavedChanges()) {
      Swal.fire({
        title: 'Unsaved Changes',
        text: 'Please save all changes before exiting.',
        icon: 'warning',
        confirmButtonText: 'Close',
      });
    } else {
      if (formData?.userType == 'Solicitor') {
        setOpenprofile(false);
      } else {
        history.goBack();
      }
    }
  };

  const handleEnableEdit = (field) => {
    if (editableField && editableField !== field) {
      setErrors((prev) => ({
        ...prev,
        [editableField]: 'Please confirm this field before editing another.',
      }));
      return;
    }

    setErrors((prev) => ({ ...prev, [field]: '' }));
    setEditableField(field);

    setTimeout(() => {
      if (inputRefs[field]?.current) {
        const input = inputRefs[field].current;
        input.focus();
        input.setSelectionRange(input.value.length, input.value.length);
      }
    }, 0);
  };

  const handleConfirmEdit = (field) => {
    if (field === 'mobileNumber') {
      const mobileNumber = formData?.mobileNumber?.trim();

      if (!mobileNumber) {
        setErrors((prev) => ({
          ...prev,
          mobileNumber: 'Mobile number is required',
        }));
        return;
      }

      if (!MOBILE_REGEX.test(mobileNumber)) {
        setErrors((prev) => ({
          ...prev,
          mobileNumber: 'Mobile number format should be: 04XX XXX XXX',
        }));
        return;
      }
    }

    setErrors((prev) => ({
      ...prev,
      [field]: '',
    }));

    setEditableField(null);
  };

  const handleClosePreview = () => {
    setOpenPreview(false);
    setOriginalImageFile(null);
  };

  const handleCloseImageViewer = () => {
    setOpenImageViewer(false);
  };

  const theme = useTheme();
  const matchessm = useMediaQuery(theme.breakpoints.down('sm'));
  const userdetailsData = JSON.parse(
    localStorage.getItem('userDetail') || '{}'
  );

  useEffect(() => {
    const load = () => {
      let userdetailsLS = {};
      try {
        userdetailsLS = JSON.parse(localStorage.getItem('userDetail') || '{}');
      } catch (e) {
        userdetailsLS = {};
      }

      setInitialData((prev) => ({
        ...prev,
        firstName: userdetailsLS?.userName || '',
        lastName: userdetailsLS?.userSurName || '',
        mobileNumber: formatMobileNumber(userdetailsLS?.userMobile || ''),
      }));
      setInitialProfilePic(userdetailsLS?.profileImage || '');

      setFormData((prev) => ({
        ...prev,
        firstName: userdetailsLS?.userName || '',
        lastName: userdetailsLS?.userSurName || '',
        mobileNumber: formatMobileNumber(userdetailsLS?.userMobile || ''),
        email: userdetailsLS?.userEmail || '',
        agencyName: userdetailsLS?.agentAgencyName || 'No Agency Name selected',
        userType: userdetailsLS?.primaryUserType || '',
        userId: userdetailsLS?.userAppId || '',
        qrCodeValue: userdetailsLS?.qrCodeValue || '',
      }));

      if (userdetailsLS?.profileImage) {
        setProfilePic(userdetailsLS.profileImage);
      }
    };
    load();
  }, []);

  const userdetails = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('userDetail') || '{}');
    } catch (e) {
      return {};
    }
  }, []);

  const accesstoken =
    userdetails?.token?.original?.token ||
    userdetails?.token ||
    userdetails?.accessToken ||
    '';

  const validate = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.mobileNumber.trim()) {
      newErrors.mobileNumber = 'Mobile number is required';
    } else if (!MOBILE_REGEX.test(formData.mobileNumber)) {
      newErrors.mobileNumber = 'Mobile number format should be: 04XX XXX XXX';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    fileRejections,
    open: openFileDialog,
  } = useDropzone({
    maxSize: MAX_FILE_SIZE,
    maxFiles: 1,
    accept: { 'image/*': [] },
    multiple: false,
    noClick: true,
    disabled: isUploading,
    onDrop: (acceptedFiles) => {
      if (!acceptedFiles || acceptedFiles.length === 0) return;
      const file = acceptedFiles[0];

      if (
        profilePic &&
        typeof profilePic === 'string' &&
        profilePic.startsWith('blob:')
      ) {
        try {
          URL.revokeObjectURL(profilePic);
        } catch (e) {}
      }
      setOriginalImageFile(file);
      const previewUrl = URL.createObjectURL(file);
      setProfileFile(file);
      setProfilePic(previewUrl);
      setRejectionMessage('');
      setOpenPreview(true);
    },
  });

  useEffect(() => {
    if (!fileRejections || fileRejections.length === 0) {
      setRejectionMessage('');
      return;
    }
    const errors = fileRejections[0].errors || [];
    const msgs = errors.map((e) => {
      if (e.code === 'file-too-large')
        return `Image must be under ${Math.round(
          MAX_FILE_SIZE / (1024 * 1024)
        )}MB.`;
      if (e.code === 'file-invalid-type')
        return 'Only image files are allowed.';
      return e.message || 'Invalid file.';
    });
    setRejectionMessage(msgs.join(' '));
  }, [fileRejections]);

  useEffect(() => {
    return () => {
      if (
        profilePic &&
        typeof profilePic === 'string' &&
        profilePic.startsWith('blob:')
      ) {
        try {
          URL.revokeObjectURL(profilePic);
        } catch (e) {}
      }
    };
  }, [profilePic]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let formattedValue = value;

    if (name === 'firstName') {
      const trimmedValue = value.replace(/^\s+/, '');
      formattedValue = trimmedValue
        .split(' ')
        .map((word) => {
          if (word.length === 0) return word;
          return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
        })
        .join(' ');
    }
    if (name === 'lastName') {
      const trimmedValue = value.replace(/^\s+/, '');
      formattedValue = trimmedValue
        .split(' ')
        .map((word) => {
          if (word.length === 0) return word;
          return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
        })
        .join(' ');
    }
    if (name === 'mobileNumber') {
      formattedValue = formatMobileNumber(value);
    }

    setFormData((prev) => ({
      ...prev,
      [name]: formattedValue,
    }));

    setErrors((prev) => ({ ...prev, [name]: '' }));
    if (
      name === 'mobileNumber' &&
      value &&
      value.length >= 10 &&
      MOBILE_REGEX.test(value)
    ) {
      const inputElement = e.target;
      const rawValue = value;
      const cursorPosition = inputElement?.selectionStart || 0;
      setErrors((prev) => ({
        ...prev,
        mobileNumber: '',
      }));
      requestAnimationFrame(() => {
        if (!inputElement) return;
        const digitsBeforeCursor = rawValue
          .slice(0, cursorPosition)
          .replace(/\D/g, '').length;
        let actualCursor = 0;
        let digitCount = 0;
        while (
          digitCount < digitsBeforeCursor &&
          actualCursor < formattedValue.length
        ) {
          if (/\d/.test(formattedValue[actualCursor])) {
            digitCount++;
          }
          actualCursor++;
        }
        inputElement.setSelectionRange(actualCursor, actualCursor);
      });
    } else if (name === 'mobileNumber' && value && !MOBILE_REGEX.test(value)) {
      setErrors((prev) => ({
        ...prev,
        mobileNumber: 'Mobile number format should be: 04XX XXX XXX',
      }));
      const inputElement = e.target;
      const rawValue = value;
      const cursorPosition = inputElement?.selectionStart || 0;
      requestAnimationFrame(() => {
        if (!inputElement) return;
        const digitsBeforeCursor = rawValue
          .slice(0, cursorPosition)
          .replace(/\D/g, '').length;
        let actualCursor = 0;
        let digitCount = 0;
        while (
          digitCount < digitsBeforeCursor &&
          actualCursor < formattedValue.length
        ) {
          if (/\d/.test(formattedValue[actualCursor])) {
            digitCount++;
          }
          actualCursor++;
        }
        inputElement.setSelectionRange(actualCursor, actualCursor);
      });
    }
  };

  const handleSave = async () => {
    if (!validate()) return;

    try {
      setIsUploading(true);

      const formattedFirstName =
        formData.firstName?.charAt(0).toUpperCase() +
          formData.firstName?.slice(1)?.trim() || '';

      const formattedLastName =
        formData.lastName?.charAt(0).toUpperCase() +
          formData.lastName?.slice(1)?.trim() || '';

      const formattedMobile = formatMobileNumber(formData.mobileNumber) || '';

      const fd = new FormData();
      fd.append('firstName', formattedFirstName);
      fd.append('lastName', formattedLastName);
      fd.append('mobileNumber', formattedMobile);
      fd.append('userType', formData.userType || '');
      fd.append('email', formData.email || '');
      fd.append('loginFrom', 'Web');
      if (profilePic === '') {
        fd.append('profileImage', null);
      } else if (profileFile) {
        fd.append('profileImage', profileFile);
      }

      const res = await postApiCall(PROFILE_SETTING, fd);

      if (res?.data?.status === 1) {
        const newProfilePic = res?.data?.profileImage || profilePic;

        setInitialData({
          firstName: formattedFirstName,
          lastName: formattedLastName,
          mobileNumber: formattedMobile,
        });

        setFormData((prev) => ({
          ...prev,
          firstName: formattedFirstName,
          lastName: formattedLastName,
          mobileNumber: formattedMobile,
        }));

        setProfilePic(newProfilePic);
        setInitialProfilePic(newProfilePic);
        setProfileFile(null);

        setEditableField(null);

        localStorage.setItem('userDetail', JSON.stringify(res?.data));
        window.dispatchEvent(new Event('userProfileUpdated'));

        Swal.fire({
          title: res?.data?.heading || 'Success',
          text: res?.data?.message || 'Profile updated successfully.',
          icon: 'success',
          confirmButtonText: 'Close',
        });
      } else {
        Swal.fire({
          title: 'Error',
          text: res?.data?.message || 'Update failed',
          icon: 'error',
          confirmButtonText: 'Close',
        });
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      Swal.fire({
        title: 'Error',
        text: 'Something went wrong.',
        icon: 'error',
        confirmButtonText: 'Close',
      });
    } finally {
      setIsUploading(false);
    }
  };

  const removeSelectedImage = () => {
    if (
      profilePic &&
      typeof profilePic === 'string' &&
      profilePic.startsWith('blob:')
    ) {
      try {
        URL.revokeObjectURL(profilePic);
      } catch (e) {}
    }
    setProfileFile(null);
    if (initialProfilePic != null) {
      setProfilePic(initialProfilePic);
    } else {
      setProfilePic('');
    }
    setRejectionMessage('');
  };

  useEffect(() => {
    if (isUploading) {
      document.body.style.pointerEvents = 'none';
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.pointerEvents = 'auto';
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.body.style.pointerEvents = 'auto';
      document.body.style.overflow = 'auto';
    };
  }, [isUploading]);

  const handleImageView = () => {
    if (profilePic) {
      setOpenImageViewer(true);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#fff',
        height: '100%',
        marginLeft: !solicitor ? '-10px' : '0px',
      }}
    >
      <Grid
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          padding: '20px',
          height: '8%',
        }}
      >
        <img src={Arrow1} alt="" />
        <Typography
          onClick={handleBack}
          sx={{
            color: '#6C2AD2',
            ...poppins300,
            fontSize: '14px',
            cursor: 'pointer',
            py: '15px',
          }}
        >
          Back to Previous
        </Typography>
      </Grid>

      <Typography
        variant="h4"
        sx={{ pl: 2, display: 'flex', alignItems: 'center' }}
      >
        <ManageAccounts sx={{ fontSize: 40, mr: 1 }} />
        <Typography
          sx={{
            fontFamily: 'poppins',
            fontWeight: 600,
            pl: 2,
            fontSize: '32px',
          }}
        >
          Profile Settings
        </Typography>
      </Typography>

      <Grid container sx={{ flex: 1, mb: 30 }}>
        <Grid
          item
          xs={12}
          md={3}
          lg={3}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: { sm: 'center', md: 'flex-start', lg: 'center' },
            p: 1,
            position: 'relative',
          }}
        >
          <Box
            sx={{
              display: { xs: 'none', md: 'block' },
              position: 'absolute',
              right: 0,
              top: '20%',
              bottom: '20%',
              width: '2px',
              bgcolor: '#ddd',
              transform: 'scaleX(0.5)',
              transformOrigin: 'right',
            }}
          />

          <Box
            sx={{
              width: { xs: 200, sm: 240, md: 240, lg: 360 },
              height: { xs: 200, sm: 240, md: 240, lg: 360 },
              borderRadius: 3,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              bgcolor: isDragActive ? '#F3E8FF' : 'transparent',
              transition: 'background-color 120ms ease',
              mb: 2,
              cursor: isUploading
                ? 'default'
                : profilePic
                ? 'pointer'
                : 'default',
            }}
            onClick={isUploading ? undefined : handleImageView}
          >
            <input {...getInputProps()} />
            {profilePic ? (
              <Avatar
                src={profilePic || undefined}
                alt="Profile"
                sx={{ width: '92%', height: '92%', cursor: 'pointer' }}
              />
            ) : (
              <Avatar
                sx={{
                  width: '92%',
                  height: '92%',
                  fontFamily: 'poppins',
                  fontSize: '50px',
                  backgroundColor: '#666666',
                  color: 'white',
                }}
              >
                {formData?.firstName?.charAt(0)?.toUpperCase()}{' '}
                {formData?.lastName?.charAt(0)?.toUpperCase()}
              </Avatar>
            )}
          </Box>

          {rejectionMessage && (
            <Typography
              variant="caption"
              color="error"
              sx={{ mb: 1, fontFamily: 'poppins' }}
            >
              {rejectionMessage}
            </Typography>
          )}

          <Typography
            variant="subtitle2"
            color="text.secondary"
            mb={1}
            fontFamily={'poppins'}
          >
            User ID: {formData?.userId?.replace(/^([A-Za-z])[A-Za-z]*/, '$1')}
          </Typography>

          <NameTooltip name={formData?.firstName} surname={formData?.lastName}>
            <Typography
              variant="h6"
              fontWeight="bold"
              mt={0}
              mb={1}
              fontSize="20px"
              fontFamily="poppins"
              sx={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '100%',
                cursor: 'pointer',
              }}
            >
              {formData?.firstName} {formData?.lastName}
            </Typography>
          </NameTooltip>

          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            <Button
              variant="contained"
              color="primary"
              disabled={isUploading}
              onClick={openFileDialog}
              sx={{
                fontFamily: 'poppins',
                backgroundColor: '#6C2AD2',
                color: 'white',
                textTransform: 'none',
                ':hover': {
                  backgroundColor: '#6C2AD2',
                  color: 'white',
                },
              }}
            >
              {profilePic == ''
                ? 'Upload Profile Image'
                : profileFile
                ? 'Profile image selected'
                : 'Change profile image'}
            </Button>

            {profileFile && (
              <Button
                variant="outlined"
                color="inherit"
                disabled={isUploading}
                onClick={removeSelectedImage}
                sx={{
                  fontFamily: 'poppins',
                  backgroundColor: 'white',
                  color: '#6C2AD2',
                  borderColor: '#6C2AD2',
                  textTransform: 'none',
                }}
              >
                Cancel Upload
              </Button>
            )}
            {profilePic && !profileFile && (
              <Tooltip title="Delete Profile Image" arrow>
                <IconButton
                  disabled={isUploading}
                  onClick={() => {
                    setProfilePic('');
                    setProfileFile(null);
                  }}
                  sx={{
                    padding: '4px',
                    minWidth: 'auto',
                    color: '#8B5CF6',
                    background: 'transparent',
                    '&:hover': {
                      background: 'transparent',
                      color: '#8B5CF6',
                    },
                  }}
                >
                  <DeleteOutline
                    sx={{
                      fontSize: '32px',
                    }}
                  />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Grid>

        <Grid
          item
          xs={12}
          md={8}
          lg={8}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            p: 4,
            overflowY: 'auto',
            ml: { xs: 0, md: 8, lg: 14 },
          }}
        >
          <Typography
            variant="h5"
            gutterBottom
            sx={{
              fontSize: '24px',
              fontFamily: 'poppins',
              fontWeight: 600,
              mb: 6,
            }}
          >
            Edit Your Profile
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Typography
                gutterBottom
                sx={{
                  fontSize: matchessm ? '15px' : '20px',
                  fontFamily: 'poppins',
                  fontWeight: 600,
                }}
              >
                First Name
              </Typography>
              <TextField
                fullWidth
                name="firstName"
                value={formData?.firstName}
                error={!!errors.firstName}
                helperText={errors.firstName}
                disabled
                sx={{
                  '.MuiFormHelperText-contained': {
                    margin: '0',
                    fontFamily: 'poppins',
                  },
                  '.MuiOutlinedInput-input': {
                    padding: matchessm ? '10px 14px' : '16px 14px',
                  },
                  fontSize: matchessm ? '12px' : '18px',
                }}
                inputProps={{ maxLength: 30, style: { fontFamily: 'poppins' } }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <Typography
                gutterBottom
                sx={{
                  fontSize: matchessm ? '15px' : '20px',
                  fontFamily: 'poppins',
                  fontWeight: 600,
                }}
              >
                Last Name
              </Typography>
              <TextField
                fullWidth
                name="lastName"
                value={formData?.lastName}
                error={!!errors.lastName}
                helperText={errors.lastName}
                sx={{
                  '.MuiFormHelperText-contained': {
                    margin: '0',
                    fontFamily: 'poppins',
                  },
                  '.MuiOutlinedInput-input': {
                    padding: matchessm ? '10px 14px' : '16px 14px',
                  },
                  fontSize: matchessm ? '12px' : '18px',
                }}
                disabled
                inputProps={{ maxLength: 30, style: { fontFamily: 'poppins' } }}
              />
            </Grid>

            <Grid item xs={12} md={12} />

            <Grid item xs={12} md={4}>
              <Typography
                gutterBottom
                sx={{
                  fontSize: matchessm ? '15px' : '20px',
                  fontFamily: 'poppins',
                  fontWeight: 600,
                }}
              >
                Mobile Number
              </Typography>
              <TextField
                fullWidth
                name="mobileNumber"
                value={formData?.mobileNumber}
                onChange={handleChange}
                error={!!errors.mobileNumber}
                helperText={errors.mobileNumber}
                sx={{
                  '.MuiFormHelperText-contained': {
                    margin: '0',
                    fontFamily: 'poppins',
                  },
                  '.MuiOutlinedInput-input': {
                    padding: matchessm ? '10px 14px' : '16px 14px',
                  },
                  fontSize: matchessm ? '12px' : '18px',
                }}
                inputRef={inputRefs.mobileNumber}
                disabled={isUploading || editableField !== 'mobileNumber'}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        disabled={isUploading}
                        onClick={() =>
                          editableField === 'mobileNumber'
                            ? handleConfirmEdit('mobileNumber')
                            : handleEnableEdit('mobileNumber')
                        }
                      >
                        {editableField === 'mobileNumber' ? (
                          <TickIcon />
                        ) : (
                          <EditIcon />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                inputProps={{ style: { fontFamily: 'poppins' }, maxLength: 12 }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <Typography
                gutterBottom
                sx={{
                  fontSize: matchessm ? '15px' : '20px',
                  fontFamily: 'poppins',
                  fontWeight: 600,
                }}
              >
                Email
              </Typography>
              <TextField
                fullWidth
                name="email"
                value={formData?.email}
                sx={{
                  '.MuiFormHelperText-contained': {
                    margin: '0',
                    fontFamily: 'poppins',
                  },
                  '.MuiOutlinedInput-input': {
                    padding: matchessm ? '10px 14px' : '16px 14px',
                  },
                  fontSize: matchessm ? '12px' : '18px',
                }}
                inputProps={{ style: { fontFamily: 'poppins' } }}
                disabled
              />
            </Grid>

            <Grid item xs={12} md={12} />

            {formData?.userType === 'Agent' && (
              <>
                <Grid item xs={12} md={4}>
                  <Typography
                    gutterBottom
                    sx={{
                      fontSize: matchessm ? '15px' : '20px',
                      fontFamily: 'poppins',
                      fontWeight: 600,
                    }}
                  >
                    Agency Name
                  </Typography>
                  <TextField
                    fullWidth
                    name="agencyName"
                    value={formData?.agencyName}
                    sx={{
                      '.MuiFormHelperText-contained': {
                        margin: '0',
                        fontFamily: 'poppins',
                      },
                      '.MuiOutlinedInput-input': {
                        padding: matchessm ? '10px 14px' : '16px 14px',
                      },
                      fontSize: matchessm ? '12px' : '18px',
                    }}
                    inputProps={{ style: { fontFamily: 'poppins' } }}
                    disabled
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <Typography
                    gutterBottom
                    sx={{
                      fontSize: matchessm ? '15px' : '20px',
                      fontFamily: 'poppins',
                      fontWeight: 600,
                    }}
                  >
                    User Type
                  </Typography>
                  <TextField
                    fullWidth
                    name="userType"
                    value={formData?.userType}
                    disabled
                    sx={{
                      '.MuiFormHelperText-contained': {
                        margin: '0',
                        fontFamily: 'poppins',
                      },
                      '.MuiOutlinedInput-input': {
                        padding: matchessm ? '10px 14px' : '16px 14px',
                      },
                      fontSize: matchessm ? '12px' : '18px',
                    }}
                    inputProps={{ style: { fontFamily: 'poppins' } }}
                  />
                </Grid>
              </>
            )}
          </Grid>

          <Grid container alignItems="center">
            <Grid item xs={12} md={4}>
              <Box mt={3}>
                <Typography
                  variant="body2"
                  sx={{
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    fontFamily: 'Poppins',
                    color: '#6C2AD2',
                  }}
                  onClick={() => setOpenQR(true)}
                >
                  User QR Code
                </Typography>
              </Box>
            </Grid>
            {userdetailsData?.primaryUserType !== 'Agent' && (
              <Grid item xs={12} md={4} textAlign="end" mt={1}>
                <ViewSignature />
              </Grid>
            )}
          </Grid>

          <Box mt={4} sx={{ marginBottom: matchessm ? '100px' : 'auto' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Button
                disabled={isUploading || !hasEditedChanges}
                variant="contained"
                color="primary"
                onClick={handleSave}
                sx={{
                  fontFamily: 'poppins',
                  backgroundColor: '#6C2AD2',
                  color: 'white',
                  textTransform: 'none',
                  minWidth: '150px',
                  ':hover': {
                    backgroundColor: '#6C2AD2',
                    color: 'white',
                  },
                }}
              >
                Save Changes
              </Button>
              {isUploading && (
                <img
                  alt="Loader"
                  src={Loading}
                  className="loaderImage"
                  style={{ height: '40px', width: '40px' }}
                />
              )}
            </Box>
          </Box>
        </Grid>
      </Grid>

      <Dialog open={openQR} onClose={() => setOpenQR(false)}>
        <DialogTitle>User QR Code</DialogTitle>
        <DialogContent>
          <Typography>QR code will be displayed here.</Typography>
        </DialogContent>
      </Dialog>

      {openQR && (
        <QrCodeGenerator
          setOpenGen={setOpenQR}
          openGen={openQR}
          value={formData?.qrCodeValue}
          userAppId={formData?.userId?.replace(/^([A-Za-z])[A-Za-z]*/, '$1')}
        />
      )}

      <Dialog
        open={openImageViewer}
        onClose={handleCloseImageViewer}
        PaperProps={{
          sx: {
            maxWidth: '90vw',
            maxHeight: '90vh',
            background: 'white',
            borderRadius: '5px',
            overflow: 'hidden',
            position: 'relative',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
          },
        }}
      >
        <IconButton
          onClick={handleCloseImageViewer}
          sx={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            zIndex: 10,
            background: 'linear-gradient(135deg, #6C2AD2 0%, #75B8F7 100%)',
            color: 'white',
            width: '40px',
            height: '40px',
            transition: 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
            boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
            border: '1px solid rgba(255, 255, 255, 0.5)',
            '&:hover': {
              background: 'linear-gradient(135deg, #5a1fb8 0%, #5aa0e0 100%)',
              transform: 'scale(1.1) rotate(90deg)',
              boxShadow: '0 8px 25px rgba(108, 42, 210, 0.5)',
              border: '1px solid rgba(255, 255, 255, 0.8)',
              '& .MuiSvgIcon-root': {
                transform: 'scale(1.1)',
              },
            },
            '& .MuiSvgIcon-root': {
              fontSize: '24px',
              transition: 'transform 0.3s ease',
            },
          }}
        >
          <Close />
        </IconButton>

        <DialogContent
          sx={{
            p: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '300px',
            minWidth: '300px',
            background: 'transparent',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              height: '100%',
              p: 3,
            }}
          >
            <img
              src={profilePic}
              alt="Profile"
              style={{
                maxWidth: '100%',
                maxHeight: '85vh',
                objectFit: 'contain',
                borderRadius: '12px',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
                background: 'white',
                padding: '8px',
                border: '1px solid rgba(108, 42, 210, 0.3)',
              }}
            />
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog
        open={openPreview}
        onClose={handleClosePreview}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{
          sx: {
            maxWidth: '800px',
            maxHeight: '800px',
            padding: '20px 20px',
          },
        }}
      >
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            <PreviewImage
              handleClose={handleClosePreview}
              src={profilePic}
              originalImageFile={originalImageFile}
              getCroppedFile={(image) => {
                const previewUrl = URL.createObjectURL(image);
                setProfilePic(previewUrl);
                setProfileFile(image);
                handleClosePreview();
              }}
            />
          </DialogContentText>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ProfileSettings;
